<template>
    <div v-if="showCreateForm || editingUser" class="modal-overlay" @click="handleClose">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ editingUser ? $t('userManagement.editUser') : $t('userManagement.createNewUser') }}</h3>
          <button @click="handleClose" class="close-btn">✕</button>
        </div>
        
        <form @submit.prevent="handleSave" class="user-form">
          <div class="form-row">
            <div class="form-group">
              <label for="first_name">{{ $t('userProfile.firstName') }} *</label>
              <input 
                v-model="userForm.first_name" 
                id="first_name"
                type="text" 
                required 
                class="form-input"
              />
            </div>
            
            <div class="form-group">
              <label for="last_name">{{ $t('userProfile.lastName') }} *</label>
              <input 
                v-model="userForm.last_name" 
                id="last_name"
                type="text" 
                required 
                class="form-input"
              />
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label for="username">{{ $t('auth.username') }} *</label>
              <input 
                v-model="userForm.username" 
                id="username"
                type="text" 
                required 
                class="form-input"
              />
            </div>
            
            <div class="form-group">
              <label for="email">{{ $t('userProfile.email') }} *</label>
              <input 
                v-model="userForm.email" 
                id="email"
                type="email" 
                required 
                class="form-input"
              />
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label for="role">{{ $t('userProfile.role') }} *</label>
              <select v-model="userForm.role" id="role" required class="form-input">
                <option value="">{{ $t('userManagement.selectRole') }}</option>
                <option value="worker">{{ $t('roles.worker') }}</option>
                <option value="manager">{{ $t('roles.manager') }}</option>
              </select>
            </div>
            
            <div class="form-group">
              <label for="date_of_birth">{{ $t('userManagement.dateOfBirth') }}</label>
              <input 
                v-model="userForm.date_of_birth" 
                id="date_of_birth"
                type="date" 
                class="form-input"
              />
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label for="social_security_number">{{ $t('userManagement.socialSecurityNumber') }}</label>
              <input 
                v-model="userForm.social_security_number" 
                id="social_security_number"
                type="text" 
                class="form-input"
                placeholder="e.g. YYYYMMDD-XXXX"
              />
            </div>
            
            <div class="form-group">
              <label for="phone">{{ $t('userProfile.phone') }}</label>
              <input 
                v-model="userForm.phone" 
                id="phone"
                type="tel" 
                class="form-input"
                placeholder="e.g. +46 70 123 45 67"
              />
            </div>
          </div>
          
          <div class="form-group">
            <label for="address">{{ $t('userManagement.address') }}</label>
            <input 
              v-model="userForm.address" 
              id="address"
              type="text" 
              class="form-input"
              placeholder="e.g. Storgatan 1, 111 22 Stockholm"
            />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="employment_date">{{ $t('userManagement.employmentDate') }}</label>
              <input 
                v-model="userForm.employment_date" 
                id="employment_date"
                type="date" 
                class="form-input"
              />
            </div>

            <div class="form-group">
              <label for="employee_tax_table">{{ $t('userManagement.taxTable') }}</label>
              <input 
                v-model.number="userForm.payroll.employee_tax_table" 
                id="employee_tax_table"
                type="number" 
                min="1"
                max="99"
                step="1"
                class="form-input"
                placeholder="e.g. 30, 31, 32"
              />
              <small class="form-help">Enter tax table number from Skatteverket (e.g., 30 is standard)</small>
            </div>
          </div>

          <!-- Salary Section -->
          <div class="salary-section">
            <h4 class="salary-title">{{ $t('userManagement.salaryInformation') }}</h4>
            
            <div class="form-row">
              <div class="form-group">
                <label for="monthly_salary">
                  {{ $t('userProfile.monthlySalary') }} (SEK)
                  <span v-if="salaryValidation.monthlySalaryError" class="field-hint">*</span>
                </label>
                <input 
                  v-model.number="userForm.payroll.monthly_salary" 
                  id="monthly_salary"
                  type="number" 
                  min="0"
                  step="0.01"
                  :class="['form-input', { 'input-error': salaryValidation.monthlySalaryError }]"
                  placeholder="e.g. 25000"
                  @input="onMonthlySalaryChange"
                />
              </div>
              
              <div class="form-group">
                <label for="employment_rate">
                  {{ $t('userManagement.employmentRate') }} (%)
                  <span v-if="salaryValidation.employmentRateError" class="field-hint">*</span>
                </label>
                <input 
                  v-model.number="userForm.payroll.employment_rate" 
                  id="employment_rate"
                  type="number" 
                  min="0"
                  max="100"
                  step="0.1"
                  :class="['form-input', { 'input-error': salaryValidation.employmentRateError }]"
                  placeholder="e.g. 100"
                  @input="onEmploymentRateChange"
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label for="hourly_salary">
                  {{ $t('userManagement.hourlySalary') }} (SEK)
                  <span v-if="salaryValidation.hourlySalaryError" class="field-hint">*</span>
                </label>
                <input 
                  v-model.number="userForm.payroll.hourly_salary" 
                  id="hourly_salary"
                  type="number" 
                  min="0"
                  step="0.01"
                  :class="['form-input', { 'input-error': salaryValidation.hourlySalaryError }]"
                  placeholder="e.g. 150"
                  @input="onHourlySalaryChange"
                />
              </div>
              
              <div class="form-group">
                <label for="employment_hours_per_week">
                  {{ $t('userManagement.employmentHoursPerWeek') }}
                  <span v-if="salaryValidation.employmentHoursError" class="field-hint">*</span>
                </label>
                <input 
                  v-model.number="userForm.payroll.employment_hours_per_week" 
                  id="employment_hours_per_week"
                  type="number" 
                  min="0"
                  max="40"
                  step="any"
                  :class="['form-input', { 'input-error': salaryValidation.employmentHoursError }]"
                  placeholder="e.g. 38.25"
                  @input="onEmploymentHoursChange"
                />
              </div>
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label for="employment_type">{{ $t('userProfile.employmentType') }}</label>
              <select
                id="employment_type"
                v-model="userForm.payroll.employment_type"
                class="form-input"
              >
                <option value="">{{ $t('common.selectType') }}</option>
                <option value="full_time">{{ $t('userProfile.full_time') }}</option>
                <option value="part_time">{{ $t('userProfile.part_time') }}</option>
                <option value="call_in">{{ $t('userProfile.call_in') }}</option>
              </select>
            </div>
          </div>
          <div class="form-group" v-if="!editingUser">
            <label for="password">{{ $t('auth.password') }} *</label>
            <input 
              v-model="userForm.password" 
              id="password"
              type="password" 
              :required="!editingUser"
              class="form-input"
            />
          </div>
          
          <div class="form-actions">
            <button type="button" @click="handleClose" class="cancel-btn">
              {{ $t('common.cancel') }}
            </button>
            <button type="submit" :disabled="saving" class="save-btn">
              {{ saving ? $t('common.saving') : (editingUser ? $t('userManagement.updateUser') : $t('userManagement.createUser')) }}
            </button>
          </div>
        </form>
      </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  showCreateForm: {
    type: Boolean,
    required: true
  },
  editingUser: {
    type: Object,
    default: null
  },
  userForm: {
    type: Object,
    required: true
  },
  saving: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'save'])

// Track which field was last edited to prevent circular updates
const lastEditedField = ref(null)
const isCalculating = ref(false)

// Constants
const HOURS_PER_MONTH_100_PERCENT = 164.5
const HOURS_PER_WEEK_100_PERCENT = 38.25
const WEEKS_PER_MONTH = 4.33

// Validation state
const salaryValidation = computed(() => {
  const form = props.userForm
  return {
    monthlySalaryError: form.payroll?.monthly_salary && !form.payroll?.employment_rate,
    employmentRateError: form.payroll?.employment_rate && !form.payroll?.monthly_salary,
    hourlySalaryError: form.payroll?.hourly_salary && !form.payroll?.employment_hours_per_week,
    employmentHoursError: form.payroll?.employment_hours_per_week && !form.payroll?.hourly_salary
  }
})

function handleClose() {
  emit('close')
}

function handleSave() {
  emit('save')
}

// Salary calculation functions
function onMonthlySalaryChange() {
  if (isCalculating.value) return
  lastEditedField.value = 'monthly_salary'
  
  const monthlySalary = props.userForm.payroll.monthly_salary
  const employmentRate = props.userForm.payroll.employment_rate
  
  if (!monthlySalary || !employmentRate) return
  
  isCalculating.value = true
  
  // Calculate hours per month based on employment rate
  const hoursPerMonth = (employmentRate / 100) * HOURS_PER_MONTH_100_PERCENT
  
  // Calculate hourly salary
  props.userForm.payroll.hourly_salary = parseFloat((monthlySalary / hoursPerMonth).toFixed(2))
  
  // Calculate employment hours per week
  const hoursPerWeek = (employmentRate / 100) * HOURS_PER_WEEK_100_PERCENT
  props.userForm.payroll.employment_hours_per_week = parseFloat(hoursPerWeek.toFixed(2))
  
  isCalculating.value = false
}

function onEmploymentRateChange() {
  if (isCalculating.value) return
  lastEditedField.value = 'employment_rate'
  
  const monthlySalary = props.userForm.payroll.monthly_salary
  const employmentRate = props.userForm.payroll.employment_rate
  
  if (!monthlySalary || !employmentRate) return
  
  isCalculating.value = true
  
  // Calculate hours per month based on employment rate
  const hoursPerMonth = (employmentRate / 100) * HOURS_PER_MONTH_100_PERCENT
  
  // Calculate hourly salary
  props.userForm.payroll.hourly_salary = parseFloat((monthlySalary / hoursPerMonth).toFixed(2))
  
  // Calculate employment hours per week
  const hoursPerWeek = (employmentRate / 100) * HOURS_PER_WEEK_100_PERCENT
  props.userForm.payroll.employment_hours_per_week = parseFloat(hoursPerWeek.toFixed(2))
  
  isCalculating.value = false
}

function onHourlySalaryChange() {
  if (isCalculating.value) return
  lastEditedField.value = 'hourly_salary'
  
  const hourlySalary = props.userForm.payroll.hourly_salary
  const hoursPerWeek = props.userForm.payroll.employment_hours_per_week
  
  if (!hourlySalary || !hoursPerWeek) return
  
  isCalculating.value = true
  
  // Calculate hours per month
  const hoursPerMonth = hoursPerWeek * WEEKS_PER_MONTH
  
  // Calculate monthly salary
  props.userForm.payroll.monthly_salary = parseFloat((hourlySalary * hoursPerMonth).toFixed(2))
  
  // Calculate employment rate (1 decimal place)
  props.userForm.payroll.employment_rate = parseFloat(((hoursPerWeek / HOURS_PER_WEEK_100_PERCENT) * 100).toFixed(1))
  
  isCalculating.value = false
}

function onEmploymentHoursChange() {
  if (isCalculating.value) return
  lastEditedField.value = 'employment_hours_per_week'
  
  const hourlySalary = props.userForm.payroll.hourly_salary
  const hoursPerWeek = props.userForm.payroll.employment_hours_per_week
  
  if (!hourlySalary || !hoursPerWeek) return
  
  isCalculating.value = true
  
  // Calculate hours per month
  const hoursPerMonth = hoursPerWeek * WEEKS_PER_MONTH
  
  // Calculate monthly salary
  props.userForm.payroll.monthly_salary = parseFloat((hourlySalary * hoursPerMonth).toFixed(2))
  
  // Calculate employment rate (1 decimal place)
  props.userForm.payroll.employment_rate = parseFloat(((hoursPerWeek / HOURS_PER_WEEK_100_PERCENT) * 100).toFixed(1))
  
  isCalculating.value = false
}
</script>

<style scoped>
/* Modal Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.modal-header h3 {
  margin: 0;
  color: #333;
}

.close-btn {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  font-size: 1.2rem;
  cursor: pointer;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.close-btn:hover {
  background: rgba(239, 68, 68, 0.9);
  color: white;
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.user-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.field-hint {
  color: #ef4444;
  font-size: 1.2rem;
  font-weight: bold;
}

.form-input {
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
}

.form-input.input-error {
  border-color: #ef4444;
  background-color: #fef2f2;
}

.form-input.input-error:focus {
  border-color: #dc2626;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

/* Salary Section */
.salary-section {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 1rem 0;
}

.salary-title {
  margin: 0 0 1rem 0;
  color: #667eea;
  font-size: 1.1rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.salary-title::before {
  content: "💰";
  font-size: 1.2rem;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
}

.cancel-btn {
  padding: 0.75rem 1.5rem;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.cancel-btn:hover {
  background: #5a6268;
}

.save-btn {
  padding: 0.75rem 1.5rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.save-btn:hover:not(:disabled) {
  background: #0056b3;
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    padding: 1.5rem;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>